/**
 * BroadcastComposeButton — admin-only. Opens a compose dialog to send an
 * immediate broadcast to every logged-in staff/PM/member. Meant to live in
 * the Layout header, next to notifications.
 */
import { useState } from "react";
import { http, formatApiError } from "../lib/api";
import { toast } from "sonner";
import { useAuth } from "../context/AuthContext";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "./ui/dialog";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Megaphone } from "@phosphor-icons/react";

export default function BroadcastComposeButton() {
  const { user } = useAuth();
  const [open, setOpen] = useState(false);
  const [subject, setSubject] = useState("");
  const [body, setBody] = useState("");
  const [busy, setBusy] = useState(false);

  if (user?.role !== "admin") return null;

  const send = async (e) => {
    e.preventDefault();
    if (!subject.trim() || !body.trim()) {
      toast.error("Subject and message body are required");
      return;
    }
    setBusy(true);
    try {
      await http.post("/broadcast", { subject: subject.trim(), body: body.trim() });
      toast.success("Broadcast sent to all staff");
      setSubject("");
      setBody("");
      setOpen(false);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button
          variant="outline"
          data-testid="broadcast-compose-btn"
          className="rounded-none border-amber-500 text-amber-700 hover:bg-amber-50 font-mono uppercase tracking-widest text-[10px] px-3 py-2 gap-1.5"
        >
          <Megaphone size={12} weight="bold" /> Broadcast
        </Button>
      </DialogTrigger>
      <DialogContent className="rounded-none border-2 border-amber-600 shadow-brutal max-w-lg"
                     data-testid="broadcast-compose-modal">
        <DialogHeader>
          <div className="label-overline text-amber-600 mb-1 flex items-center gap-1.5">
            <Megaphone size={12} weight="bold" /> Compose broadcast
          </div>
          <DialogTitle className="font-display text-2xl font-black tracking-tighter uppercase">
            Message everyone
          </DialogTitle>
        </DialogHeader>

        <form onSubmit={send} className="space-y-3 mt-3">
          <label className="block">
            <div className="label-overline text-zinc-500 mb-1">Subject *</div>
            <Input
              value={subject}
              onChange={(e) => setSubject(e.target.value)}
              placeholder="e.g. Studio closed on Friday for shoot"
              className="rounded-none border-zinc-400"
              maxLength={140}
              required
              data-testid="broadcast-subject-input"
            />
          </label>
          <label className="block">
            <div className="label-overline text-zinc-500 mb-1">Message *</div>
            <textarea
              value={body}
              onChange={(e) => setBody(e.target.value)}
              placeholder="Full message. Line breaks are preserved."
              className="w-full rounded-none border border-zinc-400 p-3 text-sm min-h-[140px]"
              maxLength={4000}
              required
              data-testid="broadcast-body-input"
            />
          </label>
          <div className="text-xs text-zinc-500">
            Every logged-in team member sees this within ~45 seconds as a popup they must acknowledge.
          </div>
          <div className="flex justify-end gap-2 pt-3 border-t border-zinc-300">
            <Button
              type="button" variant="outline" onClick={() => setOpen(false)}
              className="rounded-none border-zinc-400 font-mono uppercase tracking-widest text-xs"
            >
              Cancel
            </Button>
            <Button
              type="submit" disabled={busy}
              data-testid="broadcast-send"
              className="rounded-none bg-amber-600 hover:bg-amber-700 text-white font-mono uppercase tracking-widest text-xs px-5"
            >
              <Megaphone size={12} weight="bold" className="mr-1.5" />
              {busy ? "Sending…" : "Send Broadcast"}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}
